import {
  Box,
  chakra,
  Checkbox,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { PointerEventHandler, useState } from 'react';
import { GripVertical, Plus, Trash, Trash2 } from 'lucide-react';
import { Reorder, useDragControls } from 'framer-motion';
import { QuestionKind } from '@duke-hq/svc-api-db/schemas/enums/QuestionKind.schema';
import { createEditorContentId } from './createEditorContentId';
import { IQuestionOption } from '@duke-hq/svc-api/provider/trpc/routers/tenant/IQuestionOption';
import { useSnapshot } from 'valtio';
import { flowEditorState } from '../ReactFlow/flowEditorState';

const OptionList = chakra(Reorder.Group);
const OptionRow = chakra(Reorder.Item);

// One reorderable quiz option. Drag starts only from the left grip (the row's
// dragListener is off), so the radio/checkbox and the editable text stay
// interactive.
function EditableOptionRow({
  option,
  index,
  options,
  question,
  update,
  isFlowEditor,
}: {
  option: IQuestionOption;
  index: number;
  options: Array<IQuestionOption>;
  question: { kind: QuestionKind };
  update: (newOptions: Array<IQuestionOption>) => void;
  isFlowEditor: boolean;
}) {
  const dragControls = useDragControls();
  const startDrag: PointerEventHandler<HTMLDivElement> = (e) => {
    if (options.length > 1) {
      dragControls.start(e);
    }
  };

  return (
    <OptionRow
      as="div"
      value={option}
      dragListener={false}
      dragControls={dragControls}
      listStyleType="none"
    >
      <Flex w="full" align="center" role="group">
        <Box
          opacity={0}
          _groupHover={{ opacity: options.length > 1 ? 1 : 0.5 }}
          onPointerDown={startDrag}
          cursor={options.length > 1 ? 'grab' : 'not-allowed'}
          style={{ touchAction: 'none' }}
          aria-label="Drag to reorder option"
          role="img"
        >
          <GripVertical size="20px" color="grey" />
        </Box>
        {isFlowEditor ? (
          <Box p="6px" opacity={0} _groupHover={{ opacity: options.length > 1 ? 1 : 0.5 }}>
            <Trash2
              strokeWidth={1}
              width={20}
              height={20}
              cursor={options.length > 1 ? 'pointer' : 'not-allowed'}
              onClick={() => {
                if (options.length <= 1) {
                  return;
                }
                const newOptions = options.filter((o) => o.id !== option.id);
                update(newOptions);
              }}
            />
          </Box>
        ) : null}

        <Box
          p={1}
          m={0}
          my="2px"
          // In the flow editor, match the reply-button option pill (brand-bordered,
          // rounded, hover tint) so quiz answers read as the same kind of option.
          // The page/unit-guide quiz editor keeps its plain grey row.
          borderRadius={isFlowEditor ? '2xl' : 'lg'}
          border={isFlowEditor ? '1px solid' : '2px solid'}
          borderColor={isFlowEditor ? 'brand.600' : 'gray.200'}
          color={isFlowEditor ? 'brand.600' : undefined}
          _hover={isFlowEditor ? { backgroundColor: 'brand.50' } : undefined}
          w={isFlowEditor ? 255 : 618}
          role="group"
        >
          <Flex align="center">
            {question.kind === 'SINGLE' ? (
              <Radio value={index.toString()} mr={2}></Radio>
            ) : (
              <Checkbox
                mr={2}
                isDisabled={question.kind === 'OPEN'}
                isChecked={option.isCorrect}
                onChange={
                  question.kind === 'OPEN'
                    ? undefined
                    : (ev) => {
                        option.isCorrect = ev.target.checked;
                        update(options);
                      }
                }
              ></Checkbox>
            )}
            <Editable
              w="100%"
              onChange={(newContent) => {
                option.content = newContent;
                update(options);
              }}
              defaultValue={option.content}
            >
              <EditablePreview w="100%" />
              <EditableInput />
            </Editable>
          </Flex>
        </Box>
        {!isFlowEditor && (
          <Flex className="actions" opacity={0} _groupHover={{ opacity: 1 }}>
            <Box p="6px">
              <Trash
                strokeWidth={1}
                width={20}
                height={20}
                cursor="pointer"
                onClick={() => {
                  const newOptions = options.filter((o) => o.id !== option.id);
                  update(newOptions);
                }}
              />
            </Box>
          </Flex>
        )}
      </Flex>
    </OptionRow>
  );
}

export function QuizQuestionEditable({
  options,
  update,
  question,
}: {
  options: Array<IQuestionOption>;
  update: (newOptions: Array<IQuestionOption>) => void;
  question: {
    kind: QuestionKind;
  };
}) {
  const flowEditorSnapshot = useSnapshot(flowEditorState);

  // Check if we're using the flow editor. If we are, we need to render this a bit differently
  const isFlowEditor = flowEditorSnapshot.resource?.kind === 'CONVERSATION';

  return (
    <Flex direction="column" gap="4px" w="calc(100% - 68px)" alignSelf="end">
      <RadioGroup
        onChange={(value) => {
          const selectedOption = options[parseInt(value)];
          if (!selectedOption) {
            return;
          }
          options.forEach((option) => {
            option.isCorrect = option.id === selectedOption.id;
          });

          update(options);
        }}
        value={options.findIndex((opt) => opt.isCorrect).toString()}
      >
        <OptionList as="div" axis="y" values={options} onReorder={update} listStyleType="none">
          {options.map((option: IQuestionOption, index) => (
            <EditableOptionRow
              key={option.id}
              option={option}
              index={index}
              options={options}
              question={question}
              update={update}
              isFlowEditor={isFlowEditor}
            />
          ))}
        </OptionList>
      </RadioGroup>

      <Flex
        cursor="pointer"
        onClick={() => {
          const newOptions = [
            ...options,
            {
              id: createEditorContentId(),
              content: `Option ${options.length + 1}`,
            },
          ];
          update(newOptions);
        }}
      >
        <Plus /> Add another Option
      </Flex>
    </Flex>
  );
}

export function QuizQuestionPreview({
  options,
  question,
}: {
  options: Array<IQuestionOption>;
  question: {
    kind: QuestionKind;
  };
}) {
  const [radioSelection, setRadioSelection] = useState<string | undefined>(undefined);

  const [selectedOptions, setSelectedOptions] = useState<Array<string>>([]);

  const flowEditorSnapshot = useSnapshot(flowEditorState);

  // Check if we're using the flow editor. If we are, we need to render this a bit differently
  const isFlowEditor = flowEditorSnapshot.resource?.kind === 'CONVERSATION';

  return (
    <Flex direction="column" gap="4px" w="full">
      <RadioGroup value={radioSelection} onChange={(value) => setRadioSelection(value)}>
        {options.map((option: IQuestionOption, index) => {
          return (
            <Flex key={option.id} w="full" align="center">
              <Box
                p={1}
                m={0}
                my="2px"
                borderRadius="lg"
                border="2px solid"
                borderColor="gray.200"
                w={isFlowEditor ? 300 : 618}
                role="group"
              >
                <Flex align="center">
                  {question.kind === 'SINGLE' ? (
                    <Radio value={index.toString()} mr={2}></Radio>
                  ) : (
                    <Checkbox
                      mr={2}
                      onChange={(ev) => {
                        const opts = ev.target.checked
                          ? [...selectedOptions, option.id]
                          : selectedOptions.filter((o) => o !== option.id);

                        setSelectedOptions(opts);
                      }}
                    ></Checkbox>
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: option.content,
                    }}
                  ></div>
                </Flex>
              </Box>
            </Flex>
          );
        })}
      </RadioGroup>
    </Flex>
  );
}
